/**
 * Portal chrome copy, in both supported locales.
 *
 * Only the frame lives here — navigation labels, buttons, empty states. The
 * documentation itself is versioned content and comes from Firestore or the
 * repository bundle.
 *
 * Pure and free of `server-only`: the client components import this directly
 * rather than receiving strings as props.
 */

import { ECHOPRINT, type PortalProduct, type ProductId } from '../portal/product';
import { LOCALE_LABELS, type ContentLocale } from './constants';

export interface PortalStrings {
  tagline: string;
  contents: string;
  searchLabel: string;
  searchPlaceholder: string;
  searchNoResults: string;
  clearSearch: string;
  openNavigation: string;
  installPanelTitle: string;
  installPanelSubtitle: string;
  installGuideLink: string;
  version: string;
  minimumOS: string;
  fileSize: string;
  checksum: string;
  checksumHint: string;
  checksumVerified: string;
  checksumDeclared: string;
  copy: string;
  copied: string;
  download: string;
  releaseNotes: string;
  releasedOn: string;
  noRelease: string;
  noReleaseHelp: string;
  agentTitle: string;
  agentHelp: string;
  agentDownload: string;
  signOut: string;
  adminLink: string;
  actions: string;
  localeLabel: string;
  documentUnavailable: string;
  documentUnavailableHelp: string;
  bundledNotice: string;
  unreviewedTranslation: string;
  openQuestions: (count: number) => string;
  localeFallback: (requested: string, served: string) => string;
  /**
   * `sourceCommit` is a commit in the EchoPrint *application* repository that
   * the guide was written against — never this portal's deployed commit. The
   * label must keep saying so: staging QA read the previous wording ("app
   * commit") as the deployed build and reported the wrong release as shipped.
   */
  provenance: (contentVersion: string, sourceCommit: string) => string;
  revisionLabel: (revision: string) => string;
  skipToContent: string;
  loading: string;
  errorTitle: string;
  errorHelp: string;
  retry: string;
}

const EN: PortalStrings = {
  tagline: 'Client documentation and downloads',
  contents: 'Contents',
  searchLabel: 'Search the guide',
  searchPlaceholder: 'Search sections',
  searchNoResults: 'No sections match that search.',
  clearSearch: 'Clear search',
  openNavigation: 'Open the section list',
  installPanelTitle: 'Install echoprint',
  installPanelSubtitle: 'Download the signed macOS disk image, then follow the installation steps below.',
  installGuideLink: 'Installation and first launch',
  version: 'Version',
  minimumOS: 'Requires',
  fileSize: 'Size',
  checksum: 'SHA-256',
  checksumHint: 'Verify with: shasum -a 256 <file>',
  checksumVerified: 'verified against the uploaded file',
  checksumDeclared: 'as declared by the publisher',
  copy: 'Copy',
  copied: 'Copied',
  download: 'Download for macOS',
  releaseNotes: 'Release notes',
  releasedOn: 'Published',
  noRelease: 'No build has been published yet.',
  noReleaseHelp:
    'The documentation below is current. A download will appear here as soon as a release is promoted.',
  agentTitle: 'AI assistant instructions',
  agentHelp: 'AGENT.md configures an AI agent to answer EchoPrint questions from this guide alone.',
  agentDownload: 'Download AGENT.md',
  signOut: 'Sign out',
  adminLink: 'Admin',
  actions: 'Portal actions',
  localeLabel: 'Language',
  documentUnavailable: 'The guide is not available right now.',
  documentUnavailableHelp:
    'No published revision could be read and no bundled copy was found. Contact your echoplay contact if this persists.',
  bundledNotice: 'Showing the copy bundled with this build; no revision has been published yet.',
  unreviewedTranslation: 'This translation has not been reviewed yet.',
  openQuestions: (count) =>
    count === 1
      ? '1 operational detail is still unconfirmed and marked TODO in the guide.'
      : `${count} operational details are still unconfirmed and marked TODO in the guide.`,
  localeFallback: (requested, served) => `${requested} is not available for this revision; showing ${served}.`,
  provenance: (contentVersion, sourceCommit) =>
    `Content ${contentVersion} · EchoPrint source commit ${sourceCommit}`,
  revisionLabel: (revision) => `Revision ${revision}`,
  skipToContent: 'Skip to the guide',
  loading: 'Loading the guide…',
  errorTitle: 'Something went wrong loading the portal.',
  errorHelp: 'This is usually temporary. Try again, and sign in again if the problem continues.',
  retry: 'Try again',
};

const ZH_HANS: PortalStrings = {
  tagline: '客户文档与下载',
  contents: '目录',
  searchLabel: '搜索指南',
  searchPlaceholder: '搜索章节',
  searchNoResults: '没有匹配的章节。',
  clearSearch: '清除搜索',
  openNavigation: '打开章节列表',
  installPanelTitle: '安装 echoprint',
  installPanelSubtitle: '下载已签名的 macOS 磁盘映像，然后按下方的安装步骤操作。',
  installGuideLink: '安装与首次启动',
  version: '版本',
  minimumOS: '系统要求',
  fileSize: '大小',
  checksum: 'SHA-256',
  checksumHint: '校验命令：shasum -a 256 <文件>',
  checksumVerified: '已与上传文件核对',
  checksumDeclared: '由发布者声明',
  copy: '复制',
  copied: '已复制',
  download: '下载 macOS 版',
  releaseNotes: '版本说明',
  releasedOn: '发布时间',
  noRelease: '尚未发布任何版本。',
  noReleaseHelp: '下方文档为最新内容。一旦有版本上线，下载入口会出现在这里。',
  agentTitle: 'AI 助手指令',
  agentHelp: 'AGENT.md 用于配置 AI 助手，使其仅依据本指南回答 EchoPrint 相关问题。',
  agentDownload: '下载 AGENT.md',
  signOut: '退出登录',
  adminLink: '管理',
  actions: '门户操作',
  localeLabel: '语言',
  documentUnavailable: '当前无法加载指南。',
  documentUnavailableHelp: '未能读取已发布的修订版本，也没有找到随构建打包的副本。若持续出现，请联系 echoplay 对接人。',
  bundledNotice: '当前显示随本次构建打包的副本；尚未发布任何修订版本。',
  unreviewedTranslation: '此译文尚未经过审校。',
  openQuestions: (count) => `指南中仍有 ${count} 项运营细节未确认，已标记为 TODO。`,
  localeFallback: (requested, served) => `此修订版本没有${requested}，当前显示${served}。`,
  provenance: (contentVersion, sourceCommit) =>
    `内容 ${contentVersion} · EchoPrint 源代码提交 ${sourceCommit}`,
  revisionLabel: (revision) => `修订 ${revision}`,
  skipToContent: '跳到指南正文',
  loading: '正在加载指南…',
  errorTitle: '加载门户时出错。',
  errorHelp: '这通常是临时问题。请重试；若仍未解决，请重新登录。',
  retry: '重试',
};

export const PORTAL_STRINGS: Record<ContentLocale, PortalStrings> = {
  en: EN,
  'zh-Hans': ZH_HANS,
};

/**
 * Echo Engine's chrome.
 *
 * The interface is shared, but almost none of the copy can be: the download is a
 * source ZIP rather than a signed disk image, the reader is a developer rather
 * than a print operator, and the provenance line names a different repository.
 * Overriding only the keys that differ would leave "Download for macOS" on a
 * page that serves a ZIP, so the whole record is written out.
 */
const ECHOENGINE_EN: PortalStrings = {
  ...EN,
  tagline: 'Developer documentation and downloads',
  searchLabel: 'Search the documentation',
  installPanelTitle: 'Install Echo Engine',
  installPanelSubtitle: 'Download the source archive, verify its checksum, then follow the steps below.',
  installGuideLink: 'Release packaging',
  minimumOS: 'Requires',
  checksumHint: 'Verify with: shasum -a 256 -c <file>.sha256',
  download: 'Download the ZIP',
  noRelease: 'No archive has been published yet.',
  noReleaseHelp:
    'The documentation below is current. A download will appear here as soon as a release is promoted.',
  agentTitle: 'AI assistant instructions',
  agentHelp: 'AGENT.md configures an AI agent to answer Echo Engine questions from this documentation alone.',
  documentUnavailable: 'This document is not available right now.',
  documentUnavailableHelp:
    'No published revision could be read and no bundled copy was found. Contact your echoplay contact if this persists.',
  provenance: (contentVersion, sourceCommit) =>
    `Content ${contentVersion} · Echo Engine source commit ${sourceCommit}`,
  skipToContent: 'Skip to the documentation',
  loading: 'Loading the documentation…',
};

const ECHOENGINE_ZH_HANS: PortalStrings = {
  ...ZH_HANS,
  tagline: '开发者文档与下载',
  searchLabel: '搜索文档',
  installPanelTitle: '安装 Echo Engine',
  installPanelSubtitle: '下载源码压缩包，校验其校验和，然后按下方步骤操作。',
  installGuideLink: '发布打包',
  // 系统要求 reads as "which OS", which is the EchoPrint question. What this
  // release states is a toolchain.
  minimumOS: '环境要求',
  checksumHint: '校验命令：shasum -a 256 -c <文件>.sha256',
  download: '下载 ZIP',
  noRelease: '尚未发布任何压缩包。',
  noReleaseHelp: '下方文档为最新内容。一旦有版本上线，下载入口会出现在这里。',
  agentHelp: 'AGENT.md 用于配置 AI 助手，使其仅依据本文档回答 Echo Engine 相关问题。',
  documentUnavailable: '当前无法加载此文档。',
  provenance: (contentVersion, sourceCommit) =>
    `内容 ${contentVersion} · Echo Engine 源代码提交 ${sourceCommit}`,
  skipToContent: '跳到文档正文',
  loading: '正在加载文档…',
};

export const ECHOENGINE_PORTAL_STRINGS: Record<ContentLocale, PortalStrings> = {
  en: ECHOENGINE_EN,
  'zh-Hans': ECHOENGINE_ZH_HANS,
};

const STRINGS_BY_PRODUCT: Record<ProductId, Record<ContentLocale, PortalStrings>> = {
  echoprint: PORTAL_STRINGS,
  echoengine: ECHOENGINE_PORTAL_STRINGS,
};

export function portalStrings(locale: ContentLocale, product: PortalProduct = ECHOPRINT): PortalStrings {
  return STRINGS_BY_PRODUCT[product.id][locale] ?? STRINGS_BY_PRODUCT[product.id].en;
}

/** Locale name in the locale being described, for the switch and fallback notice. */
export function localeDisplayName(locale: ContentLocale, inLocale: ContentLocale): string {
  if (inLocale === 'zh-Hans') {
    return locale === 'zh-Hans' ? '中文' : '英文';
  }

  return LOCALE_LABELS[locale].displayName;
}
